import React, { useMemo, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router'
import { t } from 'src/translations'

import {
  LinkIcon,
  CloseIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@toasttab/buffet-pui-icons'
import {
  DEFAULT_COLORS,
  FieldType,
  GOOGLE_FONTS,
  useEditor
} from '@toasttab/sites-components'
import classnames from 'classnames'
import classNames from 'classnames'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperClass } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import { SpotlightConfig } from 'src/apollo/onlineOrdering'
import {
  ButtonType,
  SpotlightBanner as SpotlightBannerType,
  SpotlightBannerContent
} from 'src/apollo/sites'
import ToastProduct from 'src/public/components/online_ordering/ToastProduct'
import Link from 'src/shared/components/common/link'
import { isLevelAA } from 'src/shared/js/colorUtils'

import Button from 'shared/components/common/button'
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useModal
} from 'shared/components/common/modal'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import {
  ScreenWidth,
  useIsMobile,
  useScreenWidth
} from 'shared/js/utils/WindowContext'

import { useBrandedAppPromo } from 'public/components/default_template/app_promo/useAppPromo'
import { getFontFaceAndFamily } from 'public/components/default_template/meta/StyleMeta'
import { useThemeColorScheme } from 'public/components/default_template/meta/useTheme'

import { useCurrentRootPath } from './useCurrentRootPath'
import { useSpotlightContext } from './SpotlightContext'
import { A11yProvider, useA11y } from '../../online_ordering/useA11y'

const EIGHT_SECONDS_MS = 8000

type Props = {
  fixed?: boolean
  style?: React.CSSProperties
}

type WrappedProps = Props & {
  route: string
}

type AppSpotlight = {
  description: string
  link?: string | null
}

const onlineOrderingPaths = ['/order', '/online']

// Don't show if
// 1. Is not enabled or has no contents
// 2. Is on a page that is not supported
export const showSpotlight = (
  spotlightInstance: Props,
  configuredSpotlight: SpotlightBannerType | undefined | null,
  configuredLegacySpotlight: SpotlightConfig | undefined | null,
  consolidatedContents: SpotlightBannerContent[],
  currentRootPath: string,
  toastProduct: ToastProduct,
  isOnlyLegacyContent?: boolean,
  appSpotlight?: AppSpotlight,
  isMenuOnly?: boolean
) => {
  const showSpotlight =
    configuredSpotlight &&
    !!configuredSpotlight?.enabled &&
    configuredSpotlight.contents?.length &&
    // Skip applicablePages check if product is not Sites
    (toastProduct !== ToastProduct.Sites ||
      isOnApplicablePage(
        currentRootPath,
        configuredSpotlight.applicablePages || [],
        isMenuOnly ?? false
      ))
  const showLegacySpotlight =
    !!configuredLegacySpotlight &&
    !!configuredLegacySpotlight.headerText &&
    configuredLegacySpotlight.headerText.length > 0 &&
    onlineOrderingPaths.includes(currentRootPath)
  // If there is no configuration, we do not show the fixed spotlight if there is only legacy content
  const sameFixed = configuredSpotlight
    ? spotlightInstance.fixed === configuredSpotlight?.fixed
    : isOnlyLegacyContent && spotlightInstance.fixed === false
  return (
    consolidatedContents.length !== 0 &&
    sameFixed &&
    (showSpotlight || showLegacySpotlight || !!appSpotlight)
  )
}

const isOnApplicablePage = (
  currentRootPath: string,
  applicablePages: string[],
  isMenuOnly: boolean
) => {
  // isMenuOnly pages only have "/menu" in the page list, but they have multiple routes that lead there.
  if (isMenuOnly) {
    return applicablePages.includes('/menu')
  }
  // accounts for sites that do not have a custom domain page and use /online for their online ordering page instead of /order
  return (
    applicablePages.includes('/order')
      ? [...applicablePages, ...onlineOrderingPaths]
      : applicablePages
  ).includes(currentRootPath)
}

export const getConsolidatedBanners = (
  configuredSpotlight: SpotlightBannerType | undefined | null,
  legacySpotlight: SpotlightConfig | undefined | null,
  appSpotlight?: AppSpotlight
) => {
  const result = []
  if (legacySpotlight?.headerText) {
    result.push({
      description: legacySpotlight.headerText,
      additionalDetails: legacySpotlight.bodyText
    })
  }
  if (configuredSpotlight?.enabled && configuredSpotlight.contents) {
    result.push(...configuredSpotlight.contents)
  }
  if (appSpotlight?.link) {
    result.push({
      description: appSpotlight.description,
      link: appSpotlight.link
    })
  }
  return result
}

const SpotlightBannerContents = ({
  description,
  additionalDetails,
  link,
  color,
  fontFamily,
  isActive,
  onModalOpen,
  onModalClose
}: SpotlightBannerContent & {
  color: string
  fontFamily: string | undefined
  isActive: boolean
  onModalOpen: () => void
  onModalClose: () => void
}) => {
  const { isOpen, onOpen, onClose } = useModal()
  const descriptionRef = useRef<HTMLDivElement | null>(null)
  const width = useScreenWidth()

  const truncatedDescription = useMemo(
    () =>
      descriptionRef.current
        ? descriptionRef.current.scrollHeight >
          descriptionRef.current.clientHeight
        : false,
    // recalculate when the window width changes to check if the description has been truncated
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [descriptionRef?.current?.scrollHeight, width]
  )

  return (
    <>
      {link && (
        <span className='icon'>
          <LinkIcon size='xs' />
        </span>
      )}
      <span
        className='description'
        style={{ color }}
        ref={descriptionRef}
        // Don't add to tab order if there is a link, since the link will be the accessible element
        tabIndex={isActive && !link ? 0 : -1}
      >
        {link ? (
          <Link style={{ color }} data-testid='spotlightLink' href={link}>
            {description}
          </Link>
        ) : (
          description
        )}
      </span>
      {truncatedDescription || additionalDetails ? (
        <Button
          variant={ButtonType.Text}
          className={classNames(
            'seeMoreButton',
            !truncatedDescription ? 'leftPadding' : ''
          )}
          onClick={() => {
            onOpen()
            onModalOpen()
          }}
          style={{ color, fontFamily }}
          aria-haspopup='dialog'
          aria-description={t('spotlight-banner.see-more.description', {
            bannerText: description
          })}
          tabIndex={isActive ? undefined : -1}
        >
          {t('spotlight-banner.see-more')}
        </Button>
      ) : null}
      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose()
          onModalClose()
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <div className='spotlightModal'>
            <div className='header'>
              <ModalCloseButton />
            </div>
            <div className='description'>{description}</div>
            <div className='additionalDetails'>{additionalDetails}</div>
          </div>
        </ModalContent>
      </Modal>
    </>
  )
}

const WrappedSpotlightBanner = (props: WrappedProps) => {
  const { useEditableRef } = useEditor()
  const { restaurant, toastProduct, ooRestaurant } = useRestaurant()
  const { isHidden, hide } = useSpotlightContext()
  const getColorFromTheme = useThemeColorScheme()
  const isMobile = useIsMobile(ScreenWidth.EXTRA_SMALL)
  const appPromoData = useBrandedAppPromo({
    skip: !!restaurant.content?.navConfig?.disableAppPromo
  })

  const navigationPrevRef = useRef<HTMLButtonElement>(null)
  const navigationNextRef = useRef<HTMLButtonElement>(null)
  const swiperRef = useRef<SwiperClass | null>(null)

  const spotlight = restaurant.content?.spotlightBanner
  const legacySpotlight = ooRestaurant?.spotlightConfig
  const currentRootPath = useCurrentRootPath(props.route)

  const { announce } = useA11y()

  const appSpotlight = useMemo(() => {
    if (!appPromoData) {
      return undefined
    }

    return {
      description: t('spotlight-banner.download', {
        appName: appPromoData.appName
      }),
      link: appPromoData.link
    }
  }, [appPromoData])

  const consolidatedContents = useMemo(() => {
    return getConsolidatedBanners(spotlight, legacySpotlight, appSpotlight)
  }, [spotlight, legacySpotlight, appSpotlight])

  const moreThanOneBanner = consolidatedContents.length > 1
  const isOnlyLegacyContent =
    !moreThanOneBanner &&
    !!legacySpotlight?.headerText &&
    legacySpotlight?.headerText?.length > 0

  const { editableRef } = useEditableRef<HTMLDivElement>({
    name: 'spotlight banner',
    displayName: 'Banner',
    path: 'content.spotlightBanner',
    actions: ['delete'],
    schema: {
      fields: [
        {
          displayName: 'isOnlyLegacyContent',
          value: isOnlyLegacyContent,
          type: FieldType.Text,
          path: ''
        }
      ]
    }
  })

  if (
    !showSpotlight(
      { ...props },
      spotlight,
      legacySpotlight,
      consolidatedContents,
      currentRootPath,
      toastProduct,
      isOnlyLegacyContent,
      appSpotlight,
      !!restaurant.config.isMenuOnly
    )
  ) {
    return null
  }

  const maybeBackgroundColor =
    toastProduct === ToastProduct.OOBasic
      ? restaurant.meta.primaryColor
      : spotlight?.backgroundColor
  const backgroundColor = getColorFromTheme((theme) => {
    return (
      theme.colorOverrides?.spotlightBannerColorOverrides?.background ??
      theme.colorScheme.background.primary
    )
    // If either the backgroundColor or the primaryColor is not defined, use the default background color
  }, maybeBackgroundColor || restaurant.meta.primaryColor || DEFAULT_COLORS.background)

  const themeTextColor = getColorFromTheme(
    (theme) => theme.colorScheme.text.onBgPrimary,
    '#000000'
  )
  // Text color will be whatever color passes the contrast check
  const textColor =
    [themeTextColor, '#FFFFFF', '#000000'].filter((color) =>
      isLevelAA(backgroundColor, color)
    )[0] || '#FFFFFF'

  const font = spotlight?.font
    ? getFontFaceAndFamily(spotlight.font)
    : undefined
  const fontLink =
    font &&
    typeof font !== 'string' &&
    font.fontFamily &&
    GOOGLE_FONTS.includes(font.fontFamily) ? (
      <link
        href={`https://fonts.googleapis.com/css?family=${font.fontFamily}:wght@400;600;700&display=swap`}
        rel='stylesheet'
      />
    ) : null

  const button = spotlight?.fixed ? (
    <Button
      aria-label={t('spotlight-banner.hide-banner')}
      data-testid='spotlightHideButton'
      variant={ButtonType.Text}
      className={classNames(
        'hideButton',
        !moreThanOneBanner ? 'singlePage' : ''
      )}
      style={{ color: textColor }}
      onClick={hide}
    >
      <CloseIcon accessibility='semantic' />
    </Button>
  ) : null

  return (
    <>
      {font ? (
        <Helmet>
          {fontLink}
          <style type='text/css'>{`${font.fontFace}`}</style>
        </Helmet>
      ) : null}
      <div
        ref={editableRef}
        hidden={isHidden}
        data-testid='spotlightBanner'
        className={classnames('spotlightBanner', {
          fixedTop: spotlight?.fixed,
          isHidden
        })}
        style={
          {
            ...props.style,
            backgroundColor,
            color: textColor,
            fontFamily: font?.fontFamily,
            '--spotlightBannerBgColor': backgroundColor,
            '--spotlightPaginationColor': textColor
          } as React.CSSProperties
        }
      >
        <div
          className='carouselWrapper'
          role='region'
          aria-label={t('spotlight-banner.label')}
          aria-roledescription='carousel'
          aria-live='off'
          onFocus={() => swiperRef.current?.autoplay.pause()}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              swiperRef.current?.autoplay.resume()
            }
          }}
        >
          <Swiper
            slidesPerGroupAuto={false}
            navigation={{
              prevEl: navigationPrevRef.current,
              nextEl: navigationNextRef.current
            }}
            pagination={{
              clickable: true,
              el: '.carouselPagination'
            }}
            loop={true}
            autoplay={{ delay: EIGHT_SECONDS_MS }}
            modules={[Navigation, Pagination, Autoplay]}
            onSwiper={(swiper) => {
              swiperRef.current = swiper
            }}
            className='spotlightCarousel'
            style={
              { '--swiper-navigation-size': '16px' } as React.CSSProperties
            }
            onNavigationNext={(swiper) => {
              announce(
                t('spotlight-banner.now-viewing', {
                  current: swiper.realIndex + 1,
                  total: swiper.slides.length
                })
              )
            }}
            onNavigationPrev={(swiper) => {
              announce(
                t('spotlight-banner.now-viewing', {
                  current: swiper.realIndex + 1,
                  total: swiper.slides.length
                })
              )
            }}
            a11y={{
              wrapperLiveRegion: false
            }}
            onBeforeInit={(swiper) => {
              if (
                typeof swiper.params.navigation !== 'boolean' &&
                swiper.params.navigation
              ) {
                swiper.params.navigation.prevEl = navigationPrevRef.current
                swiper.params.navigation.nextEl = navigationNextRef.current
              }
            }}
          >
            {button}
            {consolidatedContents.map((content, i) => (
              <SwiperSlide key={`spotlight-carousel-${i}`}>
                {({ isActive }) => (
                  <div
                    className='contentWrapper'
                    aria-roledescription='slide'
                    aria-label={t('spotlight-banner.slide-announcement', {
                      current: i + 1,
                      total: consolidatedContents.length
                    })}
                  >
                    <SpotlightBannerContents
                      {...content}
                      color={textColor}
                      isActive={isActive}
                      onModalOpen={() => {}}
                      onModalClose={() => {}}
                      fontFamily={font?.fontFamily}
                    />
                  </div>
                )}
              </SwiperSlide>
            ))}
            {moreThanOneBanner && (
              <>
                <div className='carouselPagination' />
                {!isMobile && (
                  <div className='carouselArrows' data-testid='carousel-arrows'>
                    <button
                      ref={navigationPrevRef}
                      className='carouselArrowPrev'
                      aria-label={t('spotlight-banner.button.previous')}
                      type='button'
                      data-testid='prevButton'
                    >
                      <ChevronLeftIcon accessibility='semantic' />
                    </button>
                    <button
                      ref={navigationNextRef}
                      className='carouselArrowNext'
                      aria-label={t('spotlight-banner.button.next')}
                      type='button'
                      data-testid='nextButton'
                    >
                      <ChevronRightIcon accessibility='semantic' />
                    </button>
                  </div>
                )}
              </>
            )}
          </Swiper>
        </div>
      </div>
    </>
  )
}

const EditorSpotlightBanner = (props: Props) => {
  const { url } = useEditor()

  return (
    <A11yProvider>
      <WrappedSpotlightBanner {...props} route={url} />
    </A11yProvider>
  )
}

const SitesSpotlightBanner = (props: Props) => {
  const { pathname } = useLocation()

  return (
    <A11yProvider>
      <WrappedSpotlightBanner {...props} route={pathname} />
    </A11yProvider>
  )
}

const SpotlightBanner = (props: Props) => {
  const { isEditor } = useEditor()

  return isEditor ? (
    <EditorSpotlightBanner {...props} />
  ) : (
    <SitesSpotlightBanner {...props} />
  )
}

export default SpotlightBanner
